import React, { useState, useEffect } from 'react';
import { Share, X, Smartphone, Bell } from 'lucide-react';

interface IOSInstallPromptProps {
  onDismiss: () => void;
  onInstalled: () => void;
}

export const IOSInstallPrompt: React.FC<IOSInstallPromptProps> = ({ onDismiss, onInstalled }) => {
  const [step, setStep] = useState<'install' | 'permissions'>('install');

  // Check if already installed
  useEffect(() => {
    if (window.navigator.standalone) {
      onInstalled();
    }
  }, [onInstalled]);

  const handleInstallComplete = () => {
    setStep('permissions');
  };

  const handlePermissionsComplete = () => {
    onInstalled();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div className="bg-background rounded-t-lg w-full max-w-md mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {step === 'install' ? 'Install Relay Driver App' : 'Enable Notifications'}
          </h3>
          <button
            onClick={onDismiss}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {step === 'install' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <Smartphone size={24} className="text-blue-600" />
              <div>
                <div className="font-medium text-blue-900">Add to Home Screen</div>
                <div className="text-sm text-blue-700">
                  Install this app for the best experience and push notifications
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm text-gray-700">
                <strong>To install this app:</strong>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">1</div>
                <div className="text-sm text-gray-700">
                  Tap the <Share size={16} className="inline mx-1" /> share button at the bottom of your screen
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">2</div>
                <div className="text-sm text-gray-700">
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">3</div>
                <div className="text-sm text-gray-700">
                  Tap <strong>"Add"</strong> to confirm
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleInstallComplete}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                I've Added the App
              </button>
              <button
                onClick={onDismiss}
                className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {step === 'permissions' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <Bell size={24} className="text-green-600" />
              <div>
                <div className="font-medium text-green-900">Enable Push Notifications</div>
                <div className="text-sm text-green-700">
                  Get notified instantly when new calls are assigned
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm text-gray-700">
                <strong>To enable notifications:</strong>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">1</div>
                <div className="text-sm text-gray-700">
                  Open the app from your <strong>Home Screen</strong> (not Safari)
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">2</div>
                <div className="text-sm text-gray-700">
                  Go to <strong>Settings</strong> → <strong>App Permissions</strong>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">3</div>
                <div className="text-sm text-gray-700">
                  Enable <strong>Push Notifications</strong>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handlePermissionsComplete}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                I've Enabled Notifications
              </button>
              <button
                onClick={onDismiss}
                className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};